/**
 * AWS Cognito + AgentCore hook
 *
 * Extracted from useVoice.tsx — handles lazy client init, region selection,
 * credential management, and streaming response parsing.
 */

import { useRef, useCallback, type MutableRefObject } from 'react'
import { BedrockAgentCoreClient, InvokeAgentRuntimeCommand } from '@aws-sdk/client-bedrock-agentcore'
import { CognitoIdentityClient, GetIdCommand, GetOpenIdTokenCommand } from '@aws-sdk/client-cognito-identity'
import { STSClient, AssumeRoleWithWebIdentityCommand } from '@aws-sdk/client-sts'
import type { AwsCredentialIdentity, AwsCredentialIdentityProvider } from '@smithy/types'
import { getSystemInstructions } from '../config/personality'
import { createLogger, type ResponseHandler } from '@omote/core'
import { useTranscriptStore } from './useTranscriptStore'

const logger = createLogger('AgentCore')

// ============================================================================
// Region config + credentials
// ============================================================================

interface RegionConfig {
  region: string
  runtimeArn: string
  identityPoolId: string
  unauthRoleArn: string
}

const REGION_CONFIGS: RegionConfig[] = [
  {
    region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    runtimeArn: import.meta.env.VITE_AGENT_RUNTIME_ARN || '',
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
    unauthRoleArn: import.meta.env.VITE_COGNITO_UNAUTH_ROLE_ARN || '',
  },
  ...(import.meta.env.VITE_AGENT_RUNTIME_ARN_WEST ? [{
    region: 'us-west-2',
    runtimeArn: import.meta.env.VITE_AGENT_RUNTIME_ARN_WEST,
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID_WEST || '',
    unauthRoleArn: import.meta.env.VITE_COGNITO_UNAUTH_ROLE_ARN_WEST || '',
  }] : []),
].filter(r => r.runtimeArn && r.identityPoolId && r.unauthRoleArn)

const IDENTITY_ID_KEY = 'omote-cognito-identity-id'
let capturedIdentityId = localStorage.getItem(IDENTITY_ID_KEY) || ''

function cognitoBasicFlowCredentials(config: {
  identityPoolId: string
  unauthRoleArn: string
  region: string
}): AwsCredentialIdentityProvider {
  let cached: AwsCredentialIdentity | null = null

  return async () => {
    if (cached?.expiration && cached.expiration.getTime() > Date.now() + 5 * 60 * 1000) {
      return cached
    }

    const cognitoClient = new CognitoIdentityClient({ region: config.region })

    let IdentityId = localStorage.getItem(IDENTITY_ID_KEY)
    if (!IdentityId) {
      const resp = await cognitoClient.send(new GetIdCommand({ IdentityPoolId: config.identityPoolId }))
      IdentityId = resp.IdentityId || null
      if (IdentityId) localStorage.setItem(IDENTITY_ID_KEY, IdentityId)
    }
    capturedIdentityId = IdentityId || ''

    let Token: string | undefined
    try {
      const resp = await cognitoClient.send(new GetOpenIdTokenCommand({ IdentityId: IdentityId! }))
      Token = resp.Token!
    } catch {
      localStorage.removeItem(IDENTITY_ID_KEY)
      const fresh = await cognitoClient.send(new GetIdCommand({ IdentityPoolId: config.identityPoolId }))
      IdentityId = fresh.IdentityId || null
      if (IdentityId) localStorage.setItem(IDENTITY_ID_KEY, IdentityId)
      capturedIdentityId = IdentityId || ''
      const resp = await cognitoClient.send(new GetOpenIdTokenCommand({ IdentityId: IdentityId! }))
      Token = resp.Token!
    }

    const stsClient = new STSClient({ region: config.region })
    const { Credentials } = await stsClient.send(new AssumeRoleWithWebIdentityCommand({
      RoleArn: config.unauthRoleArn,
      WebIdentityToken: Token!,
      RoleSessionName: 'VoiceAgent',
    }))

    cached = {
      accessKeyId: Credentials!.AccessKeyId!,
      secretAccessKey: Credentials!.SecretAccessKey!,
      sessionToken: Credentials!.SessionToken!,
      expiration: Credentials!.Expiration!,
    }
    return cached
  }
}

// ============================================================================
// Region selection
// ============================================================================

async function selectBestRegion(): Promise<RegionConfig> {
  if (REGION_CONFIGS.length <= 1) return REGION_CONFIGS[0]

  const cached = sessionStorage.getItem('voice-agent-region')
  if (cached) {
    const match = REGION_CONFIGS.find(r => r.region === cached)
    if (match) return match
  }

  const results = await Promise.allSettled(
    REGION_CONFIGS.map(async (config) => {
      const start = performance.now()
      try { await fetch(`https://cognito-identity.${config.region}.amazonaws.com/`, { method: 'HEAD', mode: 'no-cors' }) } catch { /* opaque */ }
      return { config, latency: performance.now() - start }
    }),
  )

  const fulfilled = results
    .filter((r): r is PromiseFulfilledResult<{ config: RegionConfig; latency: number }> => r.status === 'fulfilled')
    .sort((a, b) => a.value.latency - b.value.latency)

  const winner = fulfilled[0]?.value.config || REGION_CONFIGS[0]
  sessionStorage.setItem('voice-agent-region', winner.region)
  logger.info(`Selected region: ${winner.region}`)
  return winner
}

const bestRegionPromise = selectBestRegion()

// ============================================================================
// Warmup
// ============================================================================

async function warmUpAgent(
  clientRef: MutableRefObject<BedrockAgentCoreClient | null>,
  regionRef: MutableRefObject<RegionConfig | null>,
): Promise<void> {
  try {
    const region = await bestRegionPromise
    regionRef.current = region

    const credentials = cognitoBasicFlowCredentials({
      identityPoolId: region.identityPoolId,
      unauthRoleArn: region.unauthRoleArn,
      region: region.region,
    })

    const client = new BedrockAgentCoreClient({
      region: region.region,
      credentials,
    })
    clientRef.current = client

    await credentials()

    logger.info('Agent warm-up complete (credentials cached)', { region: region.region })
  } catch (err) {
    logger.warn('Agent warm-up failed (non-fatal)', { error: String(err) })
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useAgentCore(setError: (msg: string) => void): {
  onResponse: ResponseHandler
  warmUp: () => void
} {
  const clientRef = useRef<BedrockAgentCoreClient | null>(null)
  const regionRef = useRef<RegionConfig | null>(null)
  const setTranscript = useTranscriptStore((s) => s.setTranscript)

  const onResponse: ResponseHandler = useCallback(async ({ text, emotion, event, setEmotion, send, done, signal, sessionId }) => {
    // Lazy-init AgentCore client
    if (!clientRef.current) {
      const region = await bestRegionPromise
      regionRef.current = region
      clientRef.current = new BedrockAgentCoreClient({
        region: region.region,
        credentials: cognitoBasicFlowCredentials({
          identityPoolId: region.identityPoolId,
          unauthRoleArn: region.unauthRoleArn,
          region: region.region,
        }),
      })
    }

    const result = await clientRef.current.send(
      new InvokeAgentRuntimeCommand({
        agentRuntimeArn: regionRef.current!.runtimeArn,
        payload: new TextEncoder().encode(JSON.stringify({
          prompt: text,
          systemPrompt: getSystemInstructions(),
          ...(emotion && emotion !== 'NEUTRAL' && emotion !== 'EMO_UNKNOWN' ? { emotion } : {}),
          ...(event && event !== 'Speech' && event !== 'EVENT_UNKNOWN' ? { event } : {}),
          ...(capturedIdentityId ? { actorId: capturedIdentityId } : {}),
          sessionId,
        })),
        contentType: 'application/json',
        qualifier: 'DEFAULT',
        runtimeSessionId: sessionId,
      }),
      { abortSignal: signal },
    )

    // Parse streaming response (one JSON per line)
    const stream = result.response
    if (!stream) return

    const reader = (stream as ReadableStream<Uint8Array>).getReader()
    const decoder = new TextDecoder()
    let parseBuffer = ''

    try {
      while (true) {
        const { done: isDone, value } = await reader.read()
        if (isDone) break

        parseBuffer += decoder.decode(value, { stream: true })
        const lines = parseBuffer.split('\n')
        parseBuffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.trim()) continue
          let data: any
          try { data = JSON.parse(line) } catch { continue }

          switch (data.type) {
            case 'response_start':
              setTranscript(t => ({ ...t, assistant: data.text || '' }))
              break
            case 'response_chunk':
              if (data.text) setTranscript(t => ({ ...t, assistant: t.assistant + data.text }))
              break
            case 'audio_chunk':
              if (data.audio) {
                const binaryStr = atob(data.audio)
                const len = binaryStr.length & ~1
                const bytes = new Uint8Array(len)
                for (let i = 0; i < len; i++) bytes[i] = binaryStr.charCodeAt(i)
                await send(bytes)
              }
              if (data.isLast) await done()
              break
            case 'emotion_update':
              setEmotion?.(data.emotion)
              break
            case 'response_end':
              if (data.fullText) setTranscript(t => ({ ...t, assistant: data.fullText }))
              break
            case 'error':
              logger.error('Agent error:', data.message)
              setError(data.message)
              break
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Process remaining buffer
    if (parseBuffer.trim()) {
      try {
        const data = JSON.parse(parseBuffer)
        if (data.type === 'response_end' && data.fullText) {
          setTranscript(t => ({ ...t, assistant: data.fullText }))
        }
      } catch { /* ignore */ }
    }
  }, [setTranscript, setError])

  const warmUp = useCallback(() => {
    warmUpAgent(clientRef, regionRef)
  }, [])

  return { onResponse, warmUp }
}
